/**
 * Normalizes list-case-stages-v2 API responses.
 *
 * V2 paginated shape:
 * { data: CaseStage[], total_count, page, limit, has_next, has_previous, page_count }
 *
 * Legacy shapes:
 * - CaseStage[] (bare array)
 * - { case_stage: CaseStage[], next_token?, transition_token? }
 */
export const parseCaseStageV2List = (response) => {
  const payload = response?.data;

  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  if (Array.isArray(payload?.case_stage)) {
    return payload.case_stage;
  }

  return [];
};

/**
 * Normalizes v2 responses for settings UI that expects { case_stage, next_token, transition_token }.
 */
export const parseCaseStageV2PaginatedResponse = (response) => {
  const payload = response?.data;
  const case_stage = parseCaseStageV2List(response);

  if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
    const page = payload.page ?? 1;

    return {
      case_stage,
      total_count: payload.total_count ?? case_stage.length,
      page,
      limit: payload.limit,
      has_next: payload.has_next ?? false,
      has_previous: payload.has_previous ?? false,
      page_count: payload.page_count,
    };
  }

  return {
    case_stage,
    total_count: case_stage.length,
    page: 1,
  };
};

/** Extract id from plain uuid or composite keys like `tenant#uuid`. */
export const resolveCaseStageIdPart = (idOrSk) => {
  if (idOrSk == null || idOrSk === '') return '';
  const str = String(idOrSk);
  if (!str.includes('#')) return str;
  const part = str.split('#')[1];
  return part || str;
};

/** Stable option id for react-select equality and API payloads. */
export const resolveCaseStageOptionId = (option) => {
  if (option == null || option === '') return '';
  if (typeof option !== 'object') return resolveCaseStageIdPart(option);

  if (option.case_stage_id != null && option.case_stage_id !== '') {
    return String(option.case_stage_id);
  }
  if (option.value != null && option.value !== '') {
    return resolveCaseStageIdPart(option.value);
  }
  if (option.sk != null && option.sk !== '') {
    return resolveCaseStageIdPart(option.sk);
  }
  if (option.pk != null && option.pk !== '') {
    return resolveCaseStageIdPart(option.pk);
  }
  return '';
};

/** Map filter/API value to a stable case stage id string. */
export const toCaseStageId = (value) => {
  if (value == null || value === '') return '';
  if (typeof value === 'object') return resolveCaseStageOptionId(value);
  return resolveCaseStageIdPart(value);
};

/** Normalize one stored filter value into a react-select option object. */
export const toCaseStageFilterOption = (item) => {
  if (typeof item === 'object' && item !== null) {
    const id = toCaseStageId(item);
    const name = item.stage_name ?? item.label ?? '';
    return {
      case_stage_id: id,
      sk: item.sk ?? id,
      stage_name: name,
      label: name,
      value: id,
    };
  }
  const id = String(item);
  return { case_stage_id: id, sk: id, stage_name: '', label: '', value: id };
};

/** Multi-select filter value → option objects for CaseStageSelectV2. */
export const normalizeCaseStageMultiValue = (value) => {
  if (!value?.length) return [];
  return value.map(toCaseStageFilterOption);
};

/** Single-select filter value → option object or null for CaseStageSelectV2. */
export const normalizeCaseStageSingleValue = (value) => {
  if (value == null || value === '') return null;
  return toCaseStageFilterOption(value);
};

/** Filter value(s) → id array for report/list APIs. */
export const toCaseStageIdList = (value) => {
  if (value == null || value === '') return [];
  const list = Array.isArray(value) ? value : [value];
  return list.map(toCaseStageId).filter(Boolean);
};
